import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';

import { NetGainsWidget } from './_authenticated._main.invoicing.invoices/NetGainsWidget';
import { formatTimeScopeToInput, invoicesColumns } from './_authenticated._main.invoicing.invoices/utils';
import { PrimaryButton } from '../components/button/PrimaryButton';
import { Tab, Tabs } from '../components/Tab';
import { Table } from '../components/table/Table';
import { getDefaultTimeScopeValues } from '../components/time-scope-picker/utils';
import { SCOPE_DATA_SEARCH_PARAM_KEY, ScopeData, TimeScopePagination } from '../components/TimeScopePagination';
import { useDownloadManyInvoices } from '../hooks/useDownloadManyInvoices';
import { INVOICE_LIMIT, useFethInvoicesPage } from '../hooks/useFetchInvoices';
import { decodeObject } from '../hooks/useJSONSearchParam';
import { ToastManager } from '../services/toast/utils';
import { t } from '../services/translation/translation';

type InvoiceTab = 'all' | 'pending' | 'paid';

const minDate = new Date(2025, 0, 1).valueOf();

const tabs: Tab<InvoiceTab>[] = [
  { id: 'all', label: t('common.alle') },
  { id: 'pending', label: t('common.toPay') },
  { id: 'paid', label: t('common.paid') },
];

const TAB_SEARCH_PARAM_KEY = 'tab';

export const InvoicesRoute = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const maxDate = useMemo(() => Date.now(), []);
  const defaultTimeScope = useMemo(() => getDefaultTimeScopeValues(), []);
  const scopeData = decodeObject<ScopeData>(searchParams.get(SCOPE_DATA_SEARCH_PARAM_KEY)) ?? {
    scope: defaultTimeScope,
    mode: 'fortnight',
  };
  const activeTabKey = (searchParams.get(TAB_SEARCH_PARAM_KEY) as InvoiceTab) ?? 'all';
  const paymentStatus = activeTabKey === 'paid' ? 'PAID' : activeTabKey === 'pending' ? 'PENDING' : undefined;
  const { mutate: downloadManyInvoices, isLoading: isDowloadingInvoices } = useDownloadManyInvoices();

  const orderBy = searchParams.get('orderBy') ?? 'referenceDate.date';
  const direction = (searchParams.get('direction') ?? 'desc') as 'desc' | 'asc' | undefined;
  const currentPage = Math.max(parseInt(searchParams.get('page') ?? '1'), 1);
  const { data, isLoading, isFetching, isPreviousData } = useFethInvoicesPage(
    { ...formatTimeScopeToInput(scopeData.scope, scopeData.mode), paymentStatus, orderBy, direction },
    currentPage,
  );
  const nbPages = Math.ceil((data?.totalItems ?? 0) / INVOICE_LIMIT);

  const handleDownload = async () => {
    if (data?.items) {
      downloadManyInvoices(formatTimeScopeToInput(scopeData.scope, scopeData.mode), {
        onSuccess: () => {
          ToastManager.show({
            message: t('invoice-route.download-many-success'),
            severity: 'success',
            timer: 10000,
          });
        },
      });
    }
  };

  return (
    <div>
      <Helmet>
        <title>{t('invoices-route.title')} - Allocab</title>
      </Helmet>

      <h3 className="text-3xl font-bold dark:text-white mb-6">{t('invoices-route.title')}</h3>

      <div>
        <TimeScopePagination minDate={minDate} maxDate={maxDate} defaultTimeScope={defaultTimeScope} />

        <Tabs
          items={tabs}
          onChange={id => {
            setSearchParams(params => {
              params.set(TAB_SEARCH_PARAM_KEY, id);
              return params;
            });
          }}
          initialTab={activeTabKey}
        />

        <div className="py-2 flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between">
          <NetGainsWidget mode={scopeData.mode} paymentStatus={paymentStatus} scope={scopeData.scope} />
          {(data?.items.length ?? 0) > 0 ? (
            <PrimaryButton className="w-full lg:w-auto" onClick={handleDownload} isLoading={isDowloadingInvoices}>
              {t('common.download-all')}
            </PrimaryButton>
          ) : null}
        </div>

        <Table
          columns={invoicesColumns}
          data={data?.items}
          identifier="id"
          isLoading={isLoading}
          isFetching={isPreviousData && isFetching}
          nbPages={nbPages}
          context={undefined}
          totalItems={data?.totalItems ?? 0}
        />
      </div>
    </div>
  );
};
